/**
 * Purchase eval (AR) — fetch + cache + conversion control.
 * Server: Kimi LLM only when gallery engagement threshold is met;
 * otherwise heuristic. Client still POSTs on sessions/intent so heuristic
 * can run; gallery threshold re-triggers via PURCHASE_EVAL_REFRESH_EVENT.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getPageEngagementContext } from "@/lib/posthogEngagementContext";
import { isPostHogConfigured } from "@/lib/posthogAnalytics";
import {
  captureVisitorPurchaseEvalJourney,
  getJourneyPayloadForServer,
  journeyIntentRank,
  type JourneyIntentLevel,
} from "@/lib/posthogVisitorJourney";
import {
  applyPurchaseEvalCtaOverrides,
  clearPurchaseEvalSessionCache,
  deriveConversionControl,
  isManualArProvinceForEval,
  PURCHASE_EVAL_REFRESH_EVENT,
  readStoredPurchaseEval,
  resolvePurchaseEvalMuxOverride,
  shouldAbortConversionSurface,
  shouldHidePurchaseEvalVideo,
  writeStoredPurchaseEval,
  type ConversionControl,
  type CtaFlagsLike,
  type InterestProfileSnapshot,
  type PurchaseEvalDecision,
  type StoredPurchaseEval,
} from "@/lib/visitorPurchaseEvalStorage";
import {
  getGalleryContextForEval,
  getGalleryEngagementSnapshot,
  isGalleryPremiumRouteCandidate,
} from "@/lib/galleryEngagementSignal";
import {
  grantGalleryPremiumRouteAccess,
  hasShareGrantInSession,
  readGalleryPremiumRouteGrantFromSession,
} from "@/lib/premiumInviteAccess";

export type UseVisitorPurchaseEvalOptions = {
  enabled?: boolean;
  countryCode?: string;
  province?: string | null;
  city?: string | null;
  neighborhood?: string | null;
  currentCta?: string;
  currentMux?: string;
};

export type VisitorPurchaseEvalState = {
  decision: PurchaseEvalDecision | null;
  control: ConversionControl;
  source: "kimi" | "heuristic" | "cache" | null;
  loading: boolean;
  skipped: boolean;
  skipReason?: string;
};

function clientMeetsEvalThreshold(
  sessionCount: number,
  intentLevel: JourneyIntentLevel,
  funnelDepth: number,
  gallery?: ReturnType<typeof getGalleryEngagementSnapshot>,
): boolean {
  if (sessionCount >= 2) return true;
  if (journeyIntentRank(intentLevel) >= 2) return true;
  if (funnelDepth >= 3) return true;
  if (gallery) {
    // Casual 1–2 fotos sin profundidad/download no dispara eval por galería sola
    const downloads = gallery.download_intents ?? 0;
    if (
      gallery.unique_cloudinary_photos <= 2 &&
      gallery.photo_opens < 5 &&
      downloads < 1 &&
      gallery.zoom_events < 1 &&
      gallery.total_dwell_seconds < 12
    ) {
      // still allow sessions/intent above — fall through only if other thresholds
    } else {
      if (gallery.unique_cloudinary_photos >= 4 || gallery.photo_opens >= 5) return true;
      if (downloads >= 1 && gallery.unique_cloudinary_photos >= 2) return true;
      if (downloads >= 2) return true;
      if (gallery.intent_score >= 55 && (gallery.unique_cloudinary_photos >= 3 || downloads >= 1)) {
        return true;
      }
      if (
        (gallery.total_dwell_seconds >= 20 || gallery.max_photo_dwell_seconds >= 10) &&
        gallery.unique_cloudinary_photos >= 2
      ) {
        return true;
      }
      if (gallery.zoom_events >= 1 && gallery.unique_cloudinary_photos >= 2) return true;
      if (
        gallery.catalog_size >= 3 &&
        gallery.coverage_pct >= 60 &&
        gallery.unique_cloudinary_photos >= 3
      ) {
        return true;
      }
    }
  }
  return false;
}

function capturePurchaseEvalEvent(
  stored: StoredPurchaseEval,
  countryCode?: string,
  sessionCount?: number,
): void {
  const p = stored.interest_profile;
  captureVisitorPurchaseEvalJourney({
    decision: stored.decision,
    source: stored.source,
    countryCode,
    province: stored.province,
    city: stored.city,
    sessionCount,
    interestProfile: p,
  });

  if (!isPostHogConfigured()) return;
  try {
    const g = getGalleryContextForEval();
    const toPremium = stored.decision.route_path === "/invitacion-premium";
    window.posthog.capture("visitor_purchase_eval", {
      site: "delfuocampo",
      purchase_likelihood: stored.decision.purchase_likelihood,
      eval_action: stored.decision.action,
      eval_scope: stored.decision.scope,
      eval_source: stored.source ?? "unknown",
      /** Motivo legible para ops (ajustes de umbral / prompts). */
      eval_reasoning: stored.decision.reasoning.slice(0, 480),
      country: countryCode,
      province: stored.province,
      city: stored.city,
      mux_override: stored.decision.mux_playback_id ?? null,
      cta_mode: stored.decision.cta_mode ?? null,
      route_path: stored.decision.route_path ?? null,
      destination: toPremium
        ? "invitacion_premium"
        : stored.decision.action || "other",
      hide_cta: stored.decision.hide_cta ?? false,
      hide_video: stored.decision.hide_video ?? false,
      session_count: sessionCount ?? null,
      // —— Gallery raw engagement (cliente) ——
      gallery_photo_opens: g.gallery_photo_opens,
      gallery_catalog_size: g.gallery_catalog_size,
      gallery_coverage_pct: g.gallery_coverage_pct,
      gallery_intent_score: g.gallery_intent_score,
      gallery_intent_label: g.gallery_intent_label,
      gallery_folders_touched: g.gallery_folders_touched,
      gallery_folders: g.gallery_folders || null,
      gallery_unique_cloudinary_photos: g.gallery_unique_cloudinary_photos,
      gallery_brand_folder: g.gallery_brand_folder,
      gallery_dwell_seconds: g.gallery_dwell_seconds,
      gallery_max_photo_dwell_seconds: g.gallery_max_photo_dwell_seconds,
      gallery_photos_long_dwell: g.gallery_photos_long_dwell,
      gallery_zoom_events: g.gallery_zoom_events,
      gallery_reopens: g.gallery_reopens,
      gallery_download_intents: g.gallery_download_intents,
      gallery_download_button_clicks: g.gallery_download_button_clicks,
      gallery_download_save_attempts: g.gallery_download_save_attempts,
      gallery_download_sample_public_ids: g.gallery_download_sample_public_ids || null,
      gallery_sample_public_ids: g.gallery_sample_public_ids || null,
      // —— Server interest profile (patrones auto-tag + tier) ——
      interest_tier: p?.interest_tier ?? null,
      pattern_label: p?.pattern_label ?? null,
      dominant_theme: p?.dominant_theme ?? null,
      exclusive_ratio: p?.exclusive_ratio ?? null,
      premium_funnel_ratio: p?.premium_funnel_ratio ?? null,
      conversion_boost: p?.conversion_boost ?? null,
      has_quality_engagement: p?.has_quality_engagement ?? null,
      should_force_premium: p?.should_force_premium ?? null,
      brand_gallery_affinity: p?.brand_gallery_affinity ?? null,
      admin_gallery_bypass_authorized: p?.admin_gallery_bypass_authorized ?? null,
      admin_gallery_bypass: stored.decision.admin_gallery_bypass === true,
      download_on_exclusive: p?.download_on_exclusive ?? null,
      interest_themes: p?.themes ?? null,
      interest_funnel_hits: p?.funnel_hits ?? null,
      cloudinary_assets_enriched: p?.cloudinary_assets_enriched ?? null,
      premium_route_candidate: isGalleryPremiumRouteCandidate(),
      routed_premium: toPremium,
      prior_premium_invite:
        hasShareGrantInSession() || readGalleryPremiumRouteGrantFromSession(),
    });
  } catch {
    /* best-effort */
  }
}

function captureConversionControlApplied(
  control: ConversionControl,
  decision: PurchaseEvalDecision,
  path: string,
  source: VisitorPurchaseEvalState["source"],
): void {
  if (!control.active || !isPostHogConfigured()) return;
  try {
    window.posthog.capture("conversion_control_applied", {
      site: "delfuocampo",
      path,
      eval_action: decision.action,
      eval_source: source ?? "unknown",
      purchase_likelihood: decision.purchase_likelihood,
      control_surface: control.surface,
      boost_hero_cta: control.boostHeroCta,
      boost_vip_cta: control.boostVipCta,
      suppress_video: control.suppressVideo,
      mux_override: control.muxOverride,
      cta_mode: decision.cta_mode ?? null,
      navigate_to: control.navigateTo,
    });
  } catch {
    /* */
  }
}

export function useVisitorPurchaseEval(
  opts: UseVisitorPurchaseEvalOptions,
): VisitorPurchaseEvalState {
  const {
    enabled = true,
    countryCode,
    province,
    city,
    neighborhood,
    currentCta,
    currentMux,
  } = opts;

  const [state, setState] = useState<VisitorPurchaseEvalState>({
    decision: null,
    control: deriveConversionControl(null),
    source: null,
    loading: false,
    skipped: false,
  });
  const [refreshToken, setRefreshToken] = useState(0);
  const controlReportedRef = useRef<string | null>(null);

  const cacheKey = useMemo(
    () => `${countryCode}|${province}|${city}|${currentCta}|${currentMux}`,
    [countryCode, province, city, currentCta, currentMux],
  );

  const onRefresh = useCallback(() => {
    clearPurchaseEvalSessionCache();
    setRefreshToken((n) => n + 1);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => onRefresh();
    window.addEventListener(PURCHASE_EVAL_REFRESH_EVENT, handler);
    return () => window.removeEventListener(PURCHASE_EVAL_REFRESH_EVENT, handler);
  }, [onRefresh]);

  useEffect(() => {
    if (!enabled || countryCode !== "AR") {
      setState({
        decision: null,
        control: deriveConversionControl(null),
        source: null,
        loading: false,
        skipped: true,
        skipReason: "not_ar",
      });
      return;
    }

    if (isManualArProvinceForEval(province) && !isGalleryPremiumRouteCandidate()) {
      setState({
        decision: null,
        control: deriveConversionControl(null),
        source: null,
        loading: false,
        skipped: true,
        skipReason: "manual_geo",
      });
      return;
    }

    const journey = getJourneyPayloadForServer();
    const gallerySnapshot = getGalleryEngagementSnapshot();
    const galleryContext = getGalleryContextForEval();
    const sessionCtx = (() => {
      if (typeof localStorage === "undefined") {
        return { session_count: 1, days_since_first_visit: 0, is_returning_visitor: false };
      }
      const sessionCount = parseInt(localStorage.getItem("ph_total_sessions") || "1", 10);
      const firstVisit = localStorage.getItem("ph_first_visit");
      const daysSince = firstVisit
        ? Math.floor((Date.now() - new Date(firstVisit).getTime()) / (1000 * 60 * 60 * 24))
        : 0;
      return {
        session_count: sessionCount,
        days_since_first_visit: daysSince,
        is_returning_visitor: sessionCount > 1,
      };
    })();

    if (
      !clientMeetsEvalThreshold(
        sessionCtx.session_count,
        journey.journey_intent_level as JourneyIntentLevel,
        journey.funnel_depth ?? 0,
        gallerySnapshot,
      )
    ) {
      setState({
        decision: null,
        control: deriveConversionControl(null),
        source: null,
        loading: false,
        skipped: true,
        skipReason: "min_sessions",
      });
      return;
    }

    const cached = readStoredPurchaseEval(province || undefined, city || undefined);
    if (cached?.decision && refreshToken === 0) {
      const control = deriveConversionControl(cached.decision);
      setState({
        decision: cached.decision,
        control,
        source: "cache",
        loading: false,
        skipped: false,
      });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, skipped: false }));

    const engagement = getPageEngagementContext() as Record<string, unknown>;
    const priorPremiumInvite =
      hasShareGrantInSession() || readGalleryPremiumRouteGrantFromSession();

    void (async () => {
      try {
        const res = await fetch("/api/visitor-purchase-eval", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            country: countryCode,
            province: province || undefined,
            city: city || undefined,
            neighborhood: neighborhood || undefined,
            session_count: sessionCtx.session_count,
            days_since_first_visit: sessionCtx.days_since_first_visit,
            is_returning_visitor: sessionCtx.is_returning_visitor,
            intent_level: journey.journey_intent_level,
            funnel_depth: journey.funnel_depth,
            journey_steps: journey.journey_steps,
            journey_session_seconds: journey.journey_session_seconds,
            scroll_pct: engagement.max_scroll_pct,
            dwell_seconds: engagement.time_on_page_seconds,
            current_cta: currentCta,
            current_mux: currentMux,
            path: typeof window !== "undefined" ? window.location.pathname : "/",
            prior_premium_invite: priorPremiumInvite,
            ...galleryContext,
          }),
        });

        if (cancelled) return;

        if (!res.ok) {
          setState({
            decision: null,
            control: deriveConversionControl(null),
            source: null,
            loading: false,
            skipped: true,
            skipReason: `http_${res.status}`,
          });
          return;
        }

        const data = (await res.json()) as {
          ok?: boolean;
          skipped?: boolean;
          reason?: string;
          decision?: PurchaseEvalDecision;
          source?: "kimi" | "heuristic";
          interest_profile?: InterestProfileSnapshot;
        };

        if (data.skipped || !data.decision) {
          setState({
            decision: null,
            control: deriveConversionControl(null),
            source: null,
            loading: false,
            skipped: true,
            skipReason: data.reason || "skipped",
          });
          return;
        }

        const stored: StoredPurchaseEval = {
          decision: data.decision,
          source: data.source,
          evaluated_at: Date.now(),
          scope: data.decision.scope,
          province: province || undefined,
          city: city || undefined,
          interest_profile: data.interest_profile,
        };
        writeStoredPurchaseEval(stored);
        capturePurchaseEvalEvent(stored, countryCode, sessionCtx.session_count);

        // Admin gallery grant YA al decidir (bypass geo antes del navigate)
        if (
          data.decision.route_path === "/invitacion-premium" ||
          data.decision.admin_gallery_bypass === true
        ) {
          grantGalleryPremiumRouteAccess({
            source: data.source ?? "eval",
            interest_tier: data.interest_profile?.interest_tier,
            pattern_label: data.interest_profile?.pattern_label,
            brand_gallery_affinity: data.interest_profile?.brand_gallery_affinity,
            admin_gallery_bypass: true,
            reasoning: data.decision.reasoning,
          });
        }

        const control = deriveConversionControl(data.decision);
        setState({
          decision: data.decision,
          control,
          source: data.source ?? null,
          loading: false,
          skipped: false,
        });
      } catch {
        if (!cancelled) {
          setState({
            decision: null,
            control: deriveConversionControl(null),
            source: null,
            loading: false,
            skipped: true,
            skipReason: "fetch_error",
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    countryCode,
    province,
    city,
    neighborhood,
    currentCta,
    currentMux,
    cacheKey,
    refreshToken,
  ]);

  useEffect(() => {
    if (!state.control.active || !state.decision) return;
    const fingerprint = `${state.decision.action}|${state.decision.purchase_likelihood}|${state.control.surface}`;
    if (controlReportedRef.current === fingerprint) return;
    controlReportedRef.current = fingerprint;
    captureConversionControlApplied(
      state.control,
      state.decision,
      typeof window !== "undefined" ? window.location.pathname : "/",
      state.source,
    );
  }, [state.control, state.decision, state.source]);

  return state;
}

export function mergeCtaFlagsWithPurchaseEval<T extends CtaFlagsLike>(
  flags: T,
  decision: PurchaseEvalDecision | null | undefined,
): T {
  return applyPurchaseEvalCtaOverrides(flags, decision);
}

export {
  deriveConversionControl,
  resolvePurchaseEvalMuxOverride,
  shouldAbortConversionSurface,
  shouldHidePurchaseEvalVideo,
};
